import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { useConstants } from '@/contexts/ConstantsContext';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Phone, 
  PhoneCall, 
  UserCheck, 
  Calendar, 
  Presentation, 
  CheckCircle, 
  FileText,
  RefreshCw,
  Trophy,
  TrendingUp,
  Target,
  Activity,
  LogIn,
  Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MetricCard = ({ title, value, target, icon: Icon, color = 'primary' }) => {
  const colorClasses = {
    primary: 'bg-primary/10 text-primary',
    blue: 'bg-blue-500/10 text-blue-400',
    emerald: 'bg-emerald-500/10 text-emerald-400',
    amber: 'bg-amber-500/10 text-amber-400',
    violet: 'bg-violet-500/10 text-violet-400',
    cyan: 'bg-cyan-500/10 text-cyan-400',
    orange: 'bg-orange-500/10 text-orange-400',
  };

  const progress = target ? Math.min((value / target) * 100, 100) : null;

  return (
    <Card className="bg-card border-border">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{title}</p>
            <p className="text-3xl font-bold mt-1">{value}</p>
            {target && (
              <div className="mt-2">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Target: {target}</span>
                  <span className={progress >= 100 ? 'text-emerald-400' : 'text-muted-foreground'}>
                    {Math.round(progress)}%
                  </span>
                </div>
                <Progress value={progress} className="h-1.5" />
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Scoreboard = () => {
  const { users } = useConstants();
  const { isAdmin, isSetter, user } = useAuth();
  
  const [filters, setFilters] = useState({
    start_date: format(new Date(), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
    user_id: isSetter ? user?.id : '', // Setters are locked to their own data
  });
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePreset, setActivePreset] = useState('today');
  
  // Attendance summary (admin only)
  const [attendanceSummary, setAttendanceSummary] = useState(null);

  useEffect(() => {
    fetchScoreboard();
    if (isAdmin) {
      fetchAttendanceSummary();
    }
  }, []);

  const fetchScoreboard = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/scoreboard`, {
        ...filters,
        user_id: isSetter ? user?.id : filters.user_id // Ensure setter sees only their data
      });
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load scoreboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceSummary = async () => {
    try {
      const response = await axios.get(`${API}/attendance/summary`);
      setAttendanceSummary(response.data);
    } catch (error) {
      console.error('Failed to load attendance summary');
    }
  };

  const setPresetRange = (preset) => {
    const today = new Date();
    let start, end;

    switch (preset) {
      case 'today':
        start = end = today;
        break;
      case 'week':
        start = startOfWeek(today, { weekStartsOn: 1 });
        end = endOfWeek(today, { weekStartsOn: 1 });
        break;
      case 'month':
        start = startOfMonth(today);
        end = endOfMonth(today);
        break;
      default:
        return;
    }

    setActivePreset(preset);
    setFilters(prev => ({
      ...prev,
      start_date: format(start, 'yyyy-MM-dd'),
      end_date: format(end, 'yyyy-MM-dd'),
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setActivePreset('custom');
  };

  const applyFilters = () => {
    fetchScoreboard();
  };

  // Get user's targets (for setter view)
  const getCurrentUserStats = () => {
    if (!data?.by_user || !user?.id) return null;
    return data.by_user[user.id];
  };

  // Get sorted users by dials for leaderboard
  const sortedUsers = data?.by_user 
    ? Object.entries(data.by_user)
        .map(([id, stats]) => ({ id, ...stats }))
        .sort((a, b) => b.dials - a.dials)
    : [];

  const currentUserStats = getCurrentUserStats();

  return (
    <div className="space-y-6" data-testid="scoreboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Trophy className="w-8 h-8 text-amber-400" />
            {isSetter ? 'My Scoreboard' : 'Scoreboard'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isSetter ? 'Track your call performance' : 'Track call performance and pipeline progress'}
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border">
        <CardContent className="p-4 space-y-4">
          {/* Quick Presets */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant={activePreset === 'today' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPresetRange('today')}
              className={activePreset === 'today' ? '' : 'border-border'}
            >
              Today
            </Button>
            <Button
              variant={activePreset === 'week' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPresetRange('week')}
              className={activePreset === 'week' ? '' : 'border-border'}
            >
              This Week
            </Button>
            <Button
              variant={activePreset === 'month' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPresetRange('month')}
              className={activePreset === 'month' ? '' : 'border-border'}
            >
              This Month
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            {/* Start Date */}
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start border-border">
                    <Calendar className="w-4 h-4 mr-2" />
                    {filters.start_date}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-card border-border">
                  <CalendarPicker
                    mode="single"
                    selected={filters.start_date ? new Date(filters.start_date) : undefined}
                    onSelect={(d) => {
                      if (d) handleFilterChange('start_date', format(d, 'yyyy-MM-dd'));
                      setStartDateOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* End Date */}
            <div className="space-y-2">
              <Label>End Date</Label>
              <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start border-border">
                    <Calendar className="w-4 h-4 mr-2" />
                    {filters.end_date}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-card border-border">
                  <CalendarPicker
                    mode="single"
                    selected={filters.end_date ? new Date(filters.end_date) : undefined}
                    onSelect={(d) => {
                      if (d) handleFilterChange('end_date', format(d, 'yyyy-MM-dd'));
                      setEndDateOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* User Filter - Admin only */}
            {isAdmin && (
              <div className="space-y-2">
                <Label>User</Label>
                <Select 
                  value={filters.user_id || "all"} 
                  onValueChange={(v) => handleFilterChange('user_id', v === "all" ? "" : v)}
                >
                  <SelectTrigger className="bg-secondary border-border">
                    <SelectValue placeholder="All users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All users</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Apply Button */}
            <div className="space-y-2">
              <Label>&nbsp;</Label>
              <Button onClick={applyFilters} className="w-full" data-testid="apply-scoreboard-filters">
                <RefreshCw className="w-4 h-4 mr-2" />
                Update
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : data ? (
        <>
          {/* Setter: Show their metrics with targets */}
          {isSetter && currentUserStats && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <MetricCard 
                  title="Dials" 
                  value={currentUserStats.dials} 
                  target={currentUserStats.dials_target}
                  icon={Phone} 
                  color="primary" 
                />
                <MetricCard 
                  title="Pickups" 
                  value={currentUserStats.pickups} 
                  target={currentUserStats.pickups_target}
                  icon={PhoneCall} 
                  color="blue" 
                />
                <MetricCard 
                  title="DM Convos" 
                  value={currentUserStats.owners_reached} 
                  target={currentUserStats.dm_convos_target}
                  icon={UserCheck} 
                  color="emerald" 
                />
                <MetricCard 
                  title="Appts Set" 
                  value={currentUserStats.appts_set} 
                  target={currentUserStats.appts_booked_target}
                  icon={Calendar} 
                  color="amber" 
                />
              </div>

              {/* Conversion Rates */}
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Your Conversion Rates
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    <div className="text-center p-4 bg-secondary/30 rounded-lg">
                      <p className="text-3xl font-bold text-blue-400">
                        {currentUserStats.dials > 0 ? Math.round((currentUserStats.pickups / currentUserStats.dials) * 100) : 0}%
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">Pickup Rate</p>
                      <p className="text-xs text-muted-foreground">{currentUserStats.pickups} / {currentUserStats.dials} dials</p>
                    </div>
                    <div className="text-center p-4 bg-secondary/30 rounded-lg">
                      <p className="text-3xl font-bold text-emerald-400">
                        {currentUserStats.pickups > 0 ? Math.round((currentUserStats.owners_reached / currentUserStats.pickups) * 100) : 0}%
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">DM Reach Rate</p>
                      <p className="text-xs text-muted-foreground">{currentUserStats.owners_reached} / {currentUserStats.pickups} pickups</p>
                    </div>
                    <div className="text-center p-4 bg-secondary/30 rounded-lg">
                      <p className="text-3xl font-bold text-amber-400">
                        {currentUserStats.owners_reached > 0 ? Math.round((currentUserStats.appts_set / currentUserStats.owners_reached) * 100) : 0}%
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">Booking Rate</p>
                      <p className="text-xs text-muted-foreground">{currentUserStats.appts_set} / {currentUserStats.owners_reached} DMs</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Admin: Show totals and leaderboard */}
          {isAdmin && (
            <>
              {/* Totals Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
                <MetricCard title="Dials" value={data.totals.dials} icon={Phone} color="primary" />
                <MetricCard title="Pickups" value={data.totals.pickups} icon={PhoneCall} color="blue" />
                <MetricCard title="Owners Reached" value={data.totals.owners_reached} icon={UserCheck} color="emerald" />
                <MetricCard title="Appts Set" value={data.totals.appts_set} icon={Calendar} color="amber" />
                <MetricCard title="Presentations" value={data.totals.presentations_scheduled} icon={Presentation} color="violet" />
                <MetricCard title="Qualified" value={data.totals.qualified} icon={CheckCircle} color="cyan" />
                <MetricCard title="Contracts" value={data.totals.contracts_sent} icon={FileText} color="orange" />
              </div>

              {/* Conversion Rates */}
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Conversion Rates
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    <div className="text-center p-4 bg-secondary/30 rounded-lg">
                      <p className="text-3xl font-bold text-blue-400">
                        {data.totals.dials > 0 ? Math.round((data.totals.pickups / data.totals.dials) * 100) : 0}%
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">Pickup Rate</p>
                      <p className="text-xs text-muted-foreground">{data.totals.pickups} / {data.totals.dials} dials</p>
                    </div>
                    <div className="text-center p-4 bg-secondary/30 rounded-lg">
                      <p className="text-3xl font-bold text-emerald-400">
                        {data.totals.pickups > 0 ? Math.round((data.totals.owners_reached / data.totals.pickups) * 100) : 0}%
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">Owner Reach Rate</p>
                      <p className="text-xs text-muted-foreground">{data.totals.owners_reached} / {data.totals.pickups} pickups</p>
                    </div>
                    <div className="text-center p-4 bg-secondary/30 rounded-lg">
                      <p className="text-3xl font-bold text-amber-400">
                        {data.totals.owners_reached > 0 ? Math.round((data.totals.appts_set / data.totals.owners_reached) * 100) : 0}%
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">Booking Rate</p>
                      <p className="text-xs text-muted-foreground">{data.totals.appts_set} / {data.totals.owners_reached} owners</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Per-User Breakdown with Targets */}
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-amber-400" />
                    Leaderboard
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border">
                          <TableHead className="w-12">#</TableHead>
                          <TableHead>User</TableHead>
                          <TableHead className="text-right">Dials</TableHead>
                          <TableHead className="text-right">Pickups</TableHead>
                          <TableHead className="text-right">DM Convos</TableHead>
                          <TableHead className="text-right">Appts</TableHead>
                          <TableHead className="text-right">Presentations</TableHead>
                          <TableHead className="text-right">Qualified</TableHead>
                          <TableHead className="text-right">Contracts</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedUsers.map((userStats, index) => (
                          <TableRow key={userStats.id} className="border-border">
                            <TableCell>
                              {index === 0 && userStats.dials > 0 ? (
                                <span className="text-amber-400 text-lg">🥇</span>
                              ) : index === 1 && userStats.dials > 0 ? (
                                <span className="text-zinc-400 text-lg">🥈</span>
                              ) : index === 2 && userStats.dials > 0 ? (
                                <span className="text-amber-600 text-lg">🥉</span>
                              ) : (
                                <span className="text-muted-foreground">{index + 1}</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div>
                                <span className="font-medium">{userStats.name}</span>
                                <Badge variant="secondary" className="ml-2 text-xs">
                                  {userStats.role}
                                </Badge>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="font-mono">
                                {userStats.dials}
                                {userStats.dials_target && (
                                  <span className="text-xs text-muted-foreground ml-1">/{userStats.dials_target}</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="font-mono">
                                {userStats.pickups}
                                {userStats.pickups_target && (
                                  <span className="text-xs text-muted-foreground ml-1">/{userStats.pickups_target}</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="font-mono">
                                {userStats.owners_reached}
                                {userStats.dm_convos_target && (
                                  <span className="text-xs text-muted-foreground ml-1">/{userStats.dm_convos_target}</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="font-mono">
                                <Badge variant={userStats.appts_set > 0 ? "default" : "secondary"} className="font-mono">
                                  {userStats.appts_set}
                                </Badge>
                                {userStats.appts_booked_target && (
                                  <span className="text-xs text-muted-foreground ml-1">/{userStats.appts_booked_target}</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-mono">{userStats.presentations_scheduled}</TableCell>
                            <TableCell className="text-right font-mono">{userStats.qualified}</TableCell>
                            <TableCell className="text-right font-mono">{userStats.contracts_sent}</TableCell>
                          </TableRow>
                        ))}
                        {sortedUsers.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                              No data for selected period
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {/* Attendance Summary Widget */}
              {attendanceSummary && (
                <Card className="bg-card border-border" data-testid="attendance-summary-widget">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Activity className="w-5 h-5 text-emerald-400" />
                      Today's Attendance
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                      {attendanceSummary.summary?.map(userAtt => (
                        <div 
                          key={userAtt.user_id}
                          className={`p-3 rounded-lg border ${userAtt.is_active ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-secondary/30 border-border'}`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-2 h-2 rounded-full ${userAtt.is_active ? 'bg-emerald-400 animate-pulse' : 'bg-muted-foreground'}`} />
                            <span className="font-medium text-sm truncate">{userAtt.user_name}</span>
                          </div>
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <LogIn className="w-3 h-3" />
                                First Login
                              </span>
                              <span className="font-mono">
                                {userAtt.first_login_today 
                                  ? format(new Date(userAtt.first_login_today), 'h:mm a')
                                  : '-'
                                }
                              </span>
                            </div>
                            <div className="flex justify-between text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Last Seen
                              </span>
                              <span className="font-mono">
                                {userAtt.last_seen 
                                  ? format(new Date(userAtt.last_seen), 'h:mm a')
                                  : '-'
                                }
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                Calls
                              </span>
                              <span className="font-mono font-medium text-blue-400">{userAtt.calls_today || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Appts
                              </span>
                              <span className="font-mono font-medium text-amber-400">{userAtt.appointments_booked_today || 0}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </>
      ) : null}
    </div>
  );
};

export default Scoreboard;
